import { reviews } from './Review.constants'

export interface MatchReviewParams {
  countryCode: string
  categoryId: string
  productBrand?: string
  productModel?: string
}

/**
 * This data structure is used to lookup the review IDs for a given country, category, brand and model
 * It is a proxy object that allows for dynamic lookup of the review IDs and is
 * only meant to be used with the hardcoded reviews data.
 *
 * We will remove this once we have a proper reviews integration. This is only
 * used for the AB test.
 */
const reviewsLookup = new Proxy({} as Record<string, unknown>, {
  get(_, countryCode: string) {
    const country = reviews.find((review) => review.countryCode === countryCode)
    if (!country) return undefined

    return new Proxy({} as Record<string, unknown>, {
      get(__, categoryId: string) {
        const categoryReviews =
          country.categories[categoryId as keyof typeof country.categories]
        if (!categoryReviews || !Array.isArray(categoryReviews))
          return undefined

        return new Proxy({} as Record<string, unknown>, {
          get(___, brand: string) {
            const brandKey = brand.toLowerCase()
            const brandReviews = categoryReviews.find(
              (item) => item.brand.toLowerCase() === brandKey,
            )
            if (!brandReviews) return undefined

            return new Proxy({} as Record<string, unknown>, {
              get(____, model: string) {
                const modelKey = model.toLowerCase()
                const matchingReviews = brandReviews.reviews.filter(
                  (reviewId) =>
                    reviewId &&
                    modelKey.includes(brandReviews.model.toLowerCase()),
                )

                return matchingReviews || []
              },
            })
          },
        })
      },
    })
  },
}) as Record<string, Record<string, Record<string, Record<string, string[]>>>>

/**
 * Matches a review ID from the hardcoded list based on market, category, brand and model
 * Uses Proxy for dynamic lookup without data duplication
 */
export function getMatchingReviewId({
  countryCode,
  categoryId,
  productBrand,
  productModel,
}: MatchReviewParams): string[] | null {
  if (!countryCode || !categoryId || !productBrand || !productModel) {
    return null
  }

  // Use proxy for dynamic lookup: country -> category -> brand -> model
  const reviewIds =
    reviewsLookup[countryCode]?.[categoryId]?.[productBrand.toLowerCase()]?.[
      productModel.toLowerCase()
    ]

  return reviewIds?.length ? reviewIds : null
}
